import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import { Skeleton } from '@/common/ui/skeleton'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import { DynamicSectionType } from '@/schema'
import { DynamicPageQueryKeys } from '@/modules/client-setting/keys'
import useMutationDuplicateDynamicPagePreset from '@/modules/client-setting/api/hooks/useMutationDuplicateDynamicPagePreset'
import type { DynamicPagePresetData } from '@/modules/client-setting/type'

const SECTION_TYPE_VARIANTS: Record<
	DynamicSectionType,
	'default' | 'secondary' | 'outline'
> = {
	[DynamicSectionType.GAME_LIST]: 'secondary',
	[DynamicSectionType.LOTTO_ANNOUNCEMENT]: 'secondary',
	[DynamicSectionType.LEADERBOARD]: 'secondary',
	[DynamicSectionType.GAME_SUGGESTION]: 'secondary',
	[DynamicSectionType.FOOTBALL_MATCHES]: 'secondary',
}

interface PresetCardProps {
	preset: DynamicPagePresetData
}

export default function PresetCard({ preset }: PresetCardProps) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)
	const [showConfirm, setShowConfirm] = useState(false)

	const { mutateAsync } = useMutationDuplicateDynamicPagePreset()

	const handleDuplicate = async () => {
		setShowConfirm(false)
		setIsLoading(true)
		try {
			const newPage = await mutateAsync({ presetId: preset.id })
			await queryClient.invalidateQueries({
				queryKey: [DynamicPageQueryKeys.GetDynamicPage],
			})
			toast.success('สร้างหน้าเว็บจากเทมเพลตสำเร็จ')
			navigate(`/operator/dynamic-page/${newPage.id}`)
		} catch {
			toast.error('ไม่สามารถสร้างหน้าเว็บจากเทมเพลตได้')
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<>
			<Card className="flex flex-col overflow-hidden transition-shadow hover:shadow-md">
				{preset.thumbnailUrl ? (
					<div className="aspect-[3/4] w-full overflow-hidden bg-muted">
						<img
							src={preset.thumbnailUrl}
							alt={preset.name}
							className="h-full w-full object-contain"
						/>
					</div>
				) : (
					<div className="flex aspect-[3/4] w-full items-center justify-center bg-muted">
						<span className="text-sm text-muted-foreground">
							ไม่มีรูปตัวอย่าง
						</span>
					</div>
				)}

				<CardHeader className="pb-2">
					<CardTitle className="text-base">{preset.name}</CardTitle>
				</CardHeader>

				<CardContent className="flex-1 pb-2">
					<div className="flex flex-wrap gap-1.5">
						{preset.sections.map((section, index) => (
							<Badge key={index} variant={SECTION_TYPE_VARIANTS[section.type]}>
								{section.name}
							</Badge>
						))}
					</div>
				</CardContent>

				<CardFooter>
					<Button
						className="w-full"
						onClick={() => setShowConfirm(true)}
						disabled={isLoading}
					>
						{isLoading ? 'กำลังสร้าง...' : 'ใช้เทมเพลตนี้'}
					</Button>
				</CardFooter>
			</Card>

			<AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันใช้เทมเพลต</AlertDialogTitle>
						<AlertDialogDescription>
							การดำเนินการนี้จะคัดลอกเซ็กชันทั้งหมดจากเทมเพลต &quot;
							{preset.name}&quot; และสร้างหน้าเว็บใหม่ให้คุณโดยอัตโนมัติ
							คุณสามารถปรับแต่งเพิ่มเติมได้ภายหลัง
							(หน้าเว็บที่ถูกสร้างใหม่จะยังไม่ถูกเปิดใช้งาน หากต้องการเปิดใช้งาน
							สามารถไปเปิดได้ในหน้า &quot;ตั้งค่าหน้าเว็บ&quot;)
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter className="gap-2">
						<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction onClick={handleDuplicate}>
							ยืนยัน
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}

export function PresetCardSkeleton() {
	return (
		<Card className="flex flex-col overflow-hidden">
			<Skeleton className="aspect-[3/4] w-full" />
			<CardHeader className="pb-2">
				<Skeleton className="h-5 w-3/4" />
			</CardHeader>
			<CardContent className="flex-1 pb-2">
				<div className="flex flex-wrap gap-1.5">
					<Skeleton className="h-5 w-16" />
					<Skeleton className="h-5 w-20" />
					<Skeleton className="h-5 w-14" />
				</div>
			</CardContent>
			<CardFooter>
				<Skeleton className="h-9 w-full" />
			</CardFooter>
		</Card>
	)
}
